import React, { FunctionComponent, memo } from 'react';
import CountUp from 'react-countup';
import styled from 'styled-components';
import { Progress, Typography, Colors, AnalyticsProps } from '@inplayer-org/inplayer-ui';
import { transparentize } from 'polished';

// utils
import media from 'utils/mediaQueries';

// components
import Restricted from 'restrictions/roles/components/Restricted';

const CountUpStyled = styled(CountUp)`
  font-size: ${({ theme }) => theme.font.sizes.h1};
  color: ${({ theme }) => theme.palette.primary.main};
  padding-bottom: 1rem;
  margin: auto;

  ${media.customMaxWidth(1400)} {
    font-size: ${({ theme }) => theme.font.sizes.h3};
  }

  ${media.customMaxWidth(1275)} {
    font-size: ${({ theme }) => theme.font.sizes.h5};
  }
`;

export const StyledProgress = styled(Progress)`
  height: 18vh;
  width: 18vh;
  margin: 0 auto;
  border-radius: 50%;
  box-sizing: border-box;
`;

export const IncircleText = styled.div`
  font-size: ${({ theme }) => theme.font.sizes.h1};
  color: ${({ theme }) => theme.palette.primary.main};
  position: absolute;
  width: 18vh;
  top: auto;
`;
interface BottomCardContentProps {
  isEngagementProbability?: boolean;
}
export const BottomCardContent = styled.div<BottomCardContentProps>`
  font-size: ${({ theme, isEngagementProbability }) =>
    isEngagementProbability ? theme.font.sizes.h2 : theme.font.sizes.h1};
  color: ${({ theme }) => theme.palette.primary.main};
  padding-bottom: 1rem;
  margin: auto;

  ${media.customMaxWidth(1400)} {
    font-size: ${({ theme, isEngagementProbability }) =>
      isEngagementProbability ? theme.font.sizes.h5 : theme.font.sizes.h3};
  }

  ${media.customMaxWidth(1275)} {
    font-size: ${({ theme, isEngagementProbability }) =>
      isEngagementProbability ? theme.font.sizes.medium : theme.font.sizes.h5};
  }
`;

export const ProgressContainer = styled.div`
  position: relative;
  width: 18vh;
  display: flex;
  align-items: center;
`;

export const CardContainer = styled.div<AnalyticsProps>`
  display: grid;
  grid-template-rows: 1fr 1fr;
  border-radius: 5px;
  margin: 0 2.5rem;
  border-top: 5px solid ${({ theme }) => theme.palette.primary.main};
  align-items: center;
  background-color: white;
  box-shadow: 0 0 8px 1px ${transparentize(0.87, Colors.fontDarkGray)};
`;

export const TopCardContent = styled(Typography)`
  padding-top: 1rem;
  margin: auto;
  font-size: ${({ theme }) => theme.font.sizes.medium};
  font-weight: 500;
  text-transform: uppercase;

  ${media.customMaxWidth(1400)} {
    font-size: ${({ theme }) => theme.font.sizes.small};
  }

  ${media.customMaxWidth(1275)} {
    font-size: ${({ theme }) => theme.font.sizes.extraSmall};
  }
`;

const GridWrapper = styled.div`
  margin: 2rem 5rem;
`;

export const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-column-gap: 5rem;
`;
interface Props {
  firstStatistic: number;
  firstStatisticText: string;
  secondStatistic: number;
  secondStatisticText: string;
  thirdStatistic?: number;
  thirdStatisticText: string;
  convRate?: number;
  hasProgress?: boolean;
  isRestricted?: boolean;
  firstBoxTag: string;
  secondBoxTag: string;
  thirdBoxTag: string;
}

const TopInformation: FunctionComponent<Props> = ({
  firstStatistic,
  firstStatisticText,
  secondStatistic,
  secondStatisticText,
  thirdStatistic = 0,
  thirdStatisticText,
  convRate,
  hasProgress,
  isRestricted = false,
  firstBoxTag,
  secondBoxTag,
  thirdBoxTag,
}) => {
  return (
    <Restricted isRestricted={isRestricted}>
      <GridWrapper>
        <GridContainer>
          <CardContainer tag={firstBoxTag}>
            <TopCardContent variant="p"> {firstStatisticText}</TopCardContent>
            <CountUpStyled start={0} end={firstStatistic} separator="," duration={2} />
          </CardContainer>

          <CardContainer tag={secondBoxTag}>
            <TopCardContent variant="p"> {secondStatisticText}</TopCardContent>
            <CountUpStyled start={0} end={secondStatistic} separator="," duration={2} />
          </CardContainer>

          <CardContainer tag={thirdBoxTag}>
            <TopCardContent variant="p">{thirdStatisticText}</TopCardContent>
            {hasProgress ? (
              <BottomCardContent>
                <CountUp start={0} end={Number(convRate)} duration={2} />%{' '}
              </BottomCardContent>
            ) : (
              <CountUpStyled start={0} end={thirdStatistic} separator="," duration={2} />
            )}
          </CardContainer>
        </GridContainer>
      </GridWrapper>
    </Restricted>
  );
};

export default memo(TopInformation);
